/**
 * Client-side shopping cart.
 *
 * Persists to localStorage with the mandatory two-phase pattern: state starts
 * from a static default, and the browser value is only read inside an effect
 * after mount (never during render / never in a useState initializer).
 */
import type { FC, ReactNode } from 'react'
import type { CartItem } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'

type CartContextValue = {
    items: CartItem[]
    count: number
    subtotal: number
    isOpen: boolean
    hydrated: boolean
    addItem: (item: CartItem) => void
    removeItem: (variantId: string) => void
    updateQuantity: (variantId: string, quantity: number) => void
    clear: () => void
    openCart: () => void
    closeCart: () => void
}

const STORAGE_KEY = 'milano-cart'

const CartContext = createContext<CartContextValue | null>(null)

export const CartProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<CartItem[]>([])
    const [isOpen, setIsOpen] = useState(false)
    const [hydrated, setHydrated] = useState(false)

    useEffect(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY)
            if (saved) setItems(JSON.parse(saved) as CartItem[])
        } catch {
            /* ignore malformed storage */
        }

        setHydrated(true)
    }, [])

    useEffect(() => {
        if (!hydrated) return

        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
        } catch {
            /* storage may be unavailable */
        }
    }, [items, hydrated])

    const addItem = useCallback((item: CartItem) => {
        setItems((prev) => {
            const index = prev.findIndex((entry) => entry.variantId === item.variantId)

            if (index >= 0) {
                const next = [...prev]
                next[index] = {
                    ...next[index],
                    quantity: next[index].quantity + item.quantity
                }

                return next
            }

            return [...prev, item]
        })
    }, [])

    const removeItem = useCallback((variantId: string) => {
        setItems((prev) => prev.filter((entry) => entry.variantId !== variantId))
    }, [])

    const updateQuantity = useCallback((variantId: string, quantity: number) => {
        setItems((prev) =>
            prev
                .map((entry) =>
                    entry.variantId === variantId
                        ? { ...entry, quantity: Math.max(0, quantity) }
                        : entry
                )
                .filter((entry) => entry.quantity > 0)
        )
    }, [])

    const clear = useCallback(() => setItems([]), [])
    const openCart = useCallback(() => setIsOpen(true), [])
    const closeCart = useCallback(() => setIsOpen(false), [])

    const value = useMemo<CartContextValue>(() => {
        const count = items.reduce((total, entry) => total + entry.quantity, 0)
        const subtotal = items.reduce(
            (total, entry) => total + entry.price * entry.quantity,
            0
        )

        return {
            items,
            count,
            subtotal,
            isOpen,
            hydrated,
            addItem,
            removeItem,
            updateQuantity,
            clear,
            openCart,
            closeCart
        }
    }, [
        items,
        isOpen,
        hydrated,
        addItem,
        removeItem,
        updateQuantity,
        clear,
        openCart,
        closeCart
    ])

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCart = (): CartContextValue => {
    const context = useContext(CartContext)

    if (!context) {
        throw new Error('useCart must be used within a CartProvider')
    }

    return context
}
